'use client';

import { useCallback, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { AnimatePresence, LayoutGroup, motion, useReducedMotion } from 'framer-motion';
import { HelpCircle, X } from 'lucide-react';

import styles from '@/features/lobby/lobby.module.css';
import { cn } from '@/lib/utils';

import { LaunchLayout } from './LaunchLayout';
import { LaunchFooter } from './LaunchFooter';

import { IdentityStep } from './steps/IdentityStep';

const BoardStep = dynamic(
  () => import('./steps/BoardStep').then((m) => ({ default: m.BoardStep })),
  { loading: () => <StepChunkLoading /> },
);
const ReviewStep = dynamic(
  () => import('./steps/ReviewStep').then((m) => ({ default: m.ReviewStep })),
  { loading: () => <StepChunkLoading /> },
);
const LaunchPreview = dynamic(
  () => import('./LaunchPreview').then((m) => ({ default: m.LaunchPreview })),
  {
    loading: () => (
      <div
        className="relative min-h-[280px] overflow-hidden rounded-md border border-white/10 bg-black/25 animate-pulse sm:min-h-[360px] md:h-[440px]"
        aria-hidden
      />
    ),
    ssr: false,
  },
);

function StepChunkLoading() {
  return (
    <div
      className="min-h-[240px] w-full animate-pulse rounded-md border border-white/5 bg-white/[0.04]"
      aria-busy
      aria-label="Loading step"
    />
  );
}

import {
  LAUNCH_STEPS,
  useLaunchWizard,
  type LaunchStepId,
} from './state/use-launch-wizard';
import { useLaunchGrid } from './state/use-launch-grid';
import { CreatorOnboarding } from '../onboarding/CreatorOnboarding';
import { useCreatorOnboarding } from '../onboarding/use-onboarding-state';
import { useWallet } from '@/hooks/use-wallet';

// ---------------------------------------------------------------------------
// URL <-> step sync (see LOBBY_PLAN §16.7)
// ---------------------------------------------------------------------------

const PARAM = 'step';

function readStepFromUrl(): LaunchStepId {
  if (typeof window === 'undefined') return 'identity';
  const url = new URL(window.location.href);
  const raw = url.searchParams.get(PARAM);
  const match = LAUNCH_STEPS.find((s) => s.id === raw);
  return match?.id ?? 'identity';
}

function writeStepToUrl(id: LaunchStepId, replace = false) {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.set(PARAM, id);
  const nextHref = url.toString();
  if (replace) window.history.replaceState({ step: id }, '', nextHref);
  else window.history.pushState({ step: id }, '', nextHref);
}

// ---------------------------------------------------------------------------

export function LaunchClient() {
  const router = useRouter();
  const wizard = useLaunchWizard();
  const reduceMotion = useReducedMotion();
  const launchGrid = useLaunchGrid();
  const creatorOnboarding = useCreatorOnboarding();
  const {
    isConnected,
    connectWallet,
    connectBusy,
    isAddressLinked,
    signInActiveWallet,
  } = useWallet();

  const [currentStep, setCurrentStep] = useState<LaunchStepId>('identity');
  // Local in-flight flag for `signInActiveWallet`. Mirrors the chat-
  // panel pattern (`src/components/chat/chat-panel.tsx` lines 193-213):
  // we ignore the discriminated result on purpose and just clear the
  // flag — on any outcome the next render evaluates the gate fresh
  // (`isAddressLinked` flips true on success, stays false otherwise
  // and the user can click "Sign in" again).
  const [isSigningIn, setIsSigningIn] = useState(false);

  useEffect(() => {
    const initial = readStepFromUrl();
    setCurrentStep(initial);
    writeStepToUrl(initial, /* replace */ true);

    const onPop = (event: PopStateEvent) => {
      const fromState = (event.state as { step?: LaunchStepId } | null)?.step;
      setCurrentStep(fromState ?? readStepFromUrl());
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const goTo = useCallback(
    (id: LaunchStepId) => {
      if (!wizard.steps.includes(id)) return;
      setCurrentStep(id);
      writeStepToUrl(id);
    },
    [wizard.steps],
  );

  const advance = useCallback(() => {
    const next = wizard.nextStep(currentStep);
    if (next) goTo(next);
  }, [wizard, currentStep, goTo]);

  const retreat = useCallback(() => {
    const prev = wizard.prevStep(currentStep);
    if (prev) goTo(prev);
    else router.push('/lobby');
  }, [wizard, currentStep, goTo, router]);

  const exitToLobby = useCallback(() => {
    router.push('/lobby');
  }, [router]);

  // ---- Launch transition ------------------------------------------------
  //
  // Submit + choreography + lobby hand-off all live in `useLaunchGrid()`
  // (see ./state/use-launch-grid.ts). This component only needs to fire
  // the launch and reflect the resulting `warping` flag in the breath
  // overlay + the warp-out animation on the wizard frame.
  //
  const onLaunch = useCallback(() => {
    // Breadcrumb so a "Launch board" click is visible in the console
    // even if `launchGrid.launch` throws before `executeActions` logs.
    console.info('[LaunchClient] Launch board clicked');
    void launchGrid.launch(wizard.draft);
  }, [launchGrid, wizard.draft]);

  // Single-SIWE log-out + login that swaps the Privy session over to
  // the wagmi-active wallet. Wraps `useWallet().signInActiveWallet` —
  // see `src/hooks/wallet/link-active-wallet.ts` for the pure decision
  // tree. Discarded result on purpose; chat does the same and the
  // post-render `isAddressLinked` value is the source of truth.
  const handleSignIn = useCallback(async () => {
    setIsSigningIn(true);
    try {
      await signInActiveWallet();
    } finally {
      setIsSigningIn(false);
    }
  }, [signInActiveWallet]);

  const warping = launchGrid.warping;
  const launchError = launchGrid.error;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') retreat();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [retreat]);

  useEffect(() => {
    if (!wizard.steps.includes(currentStep)) {
      goTo(wizard.steps[wizard.steps.length - 1] ?? 'identity');
    }
  }, [wizard.steps, currentStep, goTo]);

  const validation = wizard.validate(currentStep);
  const isLastStep = currentStep === 'review';

  const stepIdx = wizard.stepIndex(currentStep);
  const visibleCount = wizard.steps.length;
  const currentMeta = LAUNCH_STEPS.find((s) => s.id === currentStep);
  const stepPosition = `${stepIdx + 1} of ${visibleCount} · ${currentMeta?.label ?? ''}`;

  return (
    <LayoutGroup id="launch-wizard">
      <div
        className={cn(
          styles.lobby,
          // `h-full min-h-0` (was `h-screen`) respects the `AppShell`-
          // provided viewport box on mobile: the wizard now sits inside
          // a flex `main` that already reserves 92px at the bottom for
          // the fixed mobile chrome. Pinning to `h-screen` here used to
          // push the bottom step controls behind the `MobileChromeBottom`
          // nav, forcing users to guess where "Next" was. On desktop
          // (`lg:`) `main` is still `flex-1` from AppShell so `h-full`
          // still fills the viewport minus the desktop Header/Footer.
          'relative flex h-full min-h-0 w-full flex-col overflow-hidden',
          'bg-[radial-gradient(ellipse_at_top,#16120a_0%,#080608_45%,#050406_100%)]',
          'text-amber-50',
        )}
      >
        <AmbientBackdrop />

        {/* Entry / exit choreography:
              · On first mount we resolve from a tiny upscale + fade so the
                hand-off from the lobby's halo feels continuous rather than
                abrupt. Transform + opacity only — no blur (expensive,
                causes repaint/scrollbar jank).
              · On Launch we play a matching-intensity recede: small scale
                inward, opacity off. The `<LaunchBreath />` overlay carries
                the actual "something just happened" feel. */}
        <motion.div
          className="relative z-10 flex flex-1 flex-col overflow-hidden"
          style={{ willChange: 'transform, opacity' }}
          initial={
            reduceMotion
              ? { opacity: 1, scale: 1 }
              : { opacity: 0, scale: 1.01 }
          }
          animate={
            warping
              ? { opacity: 0, scale: 0.985 }
              : { opacity: 1, scale: 1 }
          }
          transition={{ duration: warping ? 0.26 : 0.24, ease: [0.4, 0.0, 0.2, 1] }}
        >
          {/* Single scroll surface on mobile: the LaunchLayout form
              column drops its inner `max-h` cap on phones so the entire
              wizard flows through this `overflow-y-auto` container. The
              tail padding (`pb-4`) keeps the last form row from sitting
              flush against the LaunchFooter when scrolled to the bottom
              — without it the very last input visually butts up against
              the back/continue bar. Desktop keeps the original generous
              padding because the bordered frame still carries its own
              breathing room. */}
          <div className="relative flex flex-1 items-start overflow-y-auto px-3 py-4 pb-4 md:px-8 md:py-8">
            <div className="mx-auto w-full">
              <LaunchLayout
                compact={isLastStep}
                eyebrow={
                  <span className="font-mono">
                    Launch Board <span className="text-amber-200/30">·</span>{' '}
                    <span className="text-amber-100/75">{currentMeta?.label}</span>
                  </span>
                }
                action={
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={creatorOnboarding.reset}
                      aria-label="Replay creator intro"
                      title="Replay creator intro"
                      className={cn(
                        'flex h-7 w-7 items-center justify-center rounded-[3px]',
                        'text-amber-100/55 transition hover:bg-amber-400/10 hover:text-amber-100',
                      )}
                    >
                      <HelpCircle className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={exitToLobby}
                      aria-label="Close wizard and return to lobby"
                      className={cn(
                        'group -mr-2 flex h-7 w-7 items-center justify-center rounded-[3px]',
                        'text-amber-100/55 transition hover:bg-amber-400/10 hover:text-amber-100',
                      )}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                }
                form={
                  <StepRenderer
                    current={currentStep}
                    wizard={wizard}
                    onLaunch={onLaunch}
                    launchError={launchError}
                    isConnected={isConnected}
                    onConnect={connectWallet}
                    isAddressLinked={isAddressLinked}
                    onSignIn={handleSignIn}
                    isSigningIn={isSigningIn}
                    connectBusy={connectBusy}
                    isLaunching={launchGrid.isLaunching}
                    currentActionLabel={launchGrid.currentActionLabel}
                  />
                }
                preview={
                  isLastStep ? undefined : (
                    <LaunchPreview draft={wizard.draft} currentStep={currentStep} />
                  )
                }
              />
            </div>
          </div>

          <LaunchFooter
            onBack={retreat}
            onNext={advance}
            nextLabel="Continue"
            nextDisabled={!validation.ok}
            stepPosition={stepPosition}
            backLabel={
              wizard.prevStep(currentStep) === null ? 'Back to lobby' : 'Previous'
            }
            hideNext={isLastStep}
            // First step → "Back to lobby" duplicates the header X close on
            // mobile and steals tap space from Continue. Hide it there;
            // the header X is the mobile escape hatch.
            hideBackOnMobile={wizard.prevStep(currentStep) === null}
          />
        </motion.div>

        <LaunchBreath active={warping} reduceMotion={!!reduceMotion} />

        <CreatorOnboarding flow={creatorOnboarding} />
      </div>
    </LayoutGroup>
  );
}

// ---------------------------------------------------------------------------
// Launch transition overlay — a quiet amber breath centred on the review
// board. One soft halo + one thin ring, both fading out as we hand off to
// the lobby. Deliberately understated: no streaks, no full-screen
// wash, no blur. Reads as "the board has taken its place" rather than
// "the app is reloading".
// ---------------------------------------------------------------------------

function LaunchBreath({
  active,
  reduceMotion,
}: {
  active: boolean;
  reduceMotion: boolean;
}) {
  if (reduceMotion) return null;
  return (
    <AnimatePresence>
      {active && (
        <motion.div
          key="launch-breath"
          aria-hidden
          className="pointer-events-none fixed inset-0 z-50 overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.12, ease: 'easeOut' }}
        >
          <motion.div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{ willChange: 'transform, opacity' }}
          >
            {/* Soft central halo */}
            <motion.div
              initial={{ scale: 0.55, opacity: 0 }}
              animate={{ scale: 1.9, opacity: [0, 0.5, 0] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.38, ease: [0.22, 0.7, 0.35, 1] }}
              className="h-36 w-36 rounded-full"
              style={{
                background:
                  'radial-gradient(circle, rgba(255,220,150,0.75) 0%, rgba(255,180,80,0.25) 50%, rgba(0,0,0,0) 75%)',
                filter: 'blur(0.5px)',
              }}
            />
            {/* Thin expanding ring */}
            <motion.div
              initial={{ scale: 0.35, opacity: 0 }}
              animate={{ scale: 2.6, opacity: [0, 0.65, 0] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.44, ease: [0.22, 0.7, 0.35, 1] }}
              className="absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{
                border: '1px solid rgba(255,214,140,0.7)',
                boxShadow: '0 0 12px rgba(255,200,110,0.4)',
              }}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function StepRenderer({
  current,
  wizard,
  onLaunch,
  launchError,
  isConnected,
  onConnect,
  isAddressLinked,
  onSignIn,
  isSigningIn,
  connectBusy,
  isLaunching,
  currentActionLabel,
}: {
  current: LaunchStepId;
  wizard: ReturnType<typeof useLaunchWizard>;
  onLaunch: () => void;
  launchError: string | null;
  isConnected: boolean;
  onConnect: ReturnType<typeof useWallet>['connectWallet'];
  isAddressLinked: boolean;
  onSignIn: () => void;
  isSigningIn: boolean;
  connectBusy: boolean;
  isLaunching: boolean;
  currentActionLabel: string | null;
}) {
  switch (current) {
    case 'identity':
      return <IdentityStep draft={wizard.draft} dispatch={wizard.dispatch} />;
    case 'board':
      return <BoardStep draft={wizard.draft} dispatch={wizard.dispatch} />;
    case 'review':
      return (
        <ReviewStep
          draft={wizard.draft}
          dispatch={wizard.dispatch}
          onLaunch={onLaunch}
          error={launchError}
          isConnected={isConnected}
          onConnect={onConnect}
          isAddressLinked={isAddressLinked}
          onSignIn={onSignIn}
          isSigningIn={isSigningIn}
          connectBusy={connectBusy}
          isLaunching={isLaunching}
          currentActionLabel={currentActionLabel}
        />
      );
    default:
      return null;
  }
}

function AmbientBackdrop() {
  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.18]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,215,120,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,215,120,0.08) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage:
            'radial-gradient(ellipse at center, black 30%, transparent 80%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 h-[720px] w-[720px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(255,186,73,0.18),transparent_65%)] blur-2xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 right-10 h-[560px] w-[560px] rounded-full bg-[radial-gradient(circle,rgba(160,60,40,0.22),transparent_70%)] blur-2xl"
      />
    </>
  );
}
